import angular from 'angular';

class UserGroupsService {
    visiblePromise = null;
    visible = [];
    promise = null;
    groups = [];

    /* @ngInject */
    constructor(ApiService, $rootScope) {
        this.ApiService = ApiService;
        $rootScope.$on('logout', () => {
            this.visiblePromise = null;
            this.visible = [];
            this.promise = null;
            this.groups = [];
        });
    }

    async reloadVisible() {
        const {data} = await this.ApiService.call2(`/users/groups/visible`, null, {method: 'GET'});
        this.visible = data.groups;
    }

    loadVisible() {
        if (!this.visiblePromise) {
            this.visiblePromise = this.reloadVisible();
        }
        return this.visiblePromise;
    }

    async loadUserGroups() {
        if (!this.promise) {
            this.promise = this.reloadUserGroups();
        }
        return this.promise;
    }

    async reloadUserGroups() {
        this.reloadVisible();
        const response = await this.ApiService.call2('/users/groups/list', null, {method: 'GET'});
        this.groups = response.data.groups;
        return this.groups;
    }

    async deleteUserGroup(idUserGroup) {
        const {data} = await this.ApiService.call2(`/users/groups/${idUserGroup}/delete`, null, {method: 'DELETE'});
        if (data.success) {
            const index = this.groups.findIndex(group => group.idUserGroup === idUserGroup);
            if (index >= 0) {
                this.groups.splice(index, 1);
            }
        }
    }

    async createUserGroup(userGroupData) {
        const {data} = await this.ApiService.call2(`/users/groups/create`, userGroupData);
        this.groups.push(data.userGroup);
        return data.userGroup;
    }

    async updateUserGroup(idUserGroup, userGroupData) {
        const {data} = await this.ApiService.call2(`/users/groups/${idUserGroup}/patch`, userGroupData, {throwOnError: true});
        const {userGroup} = data;
        const index = this.groups.findIndex(g => g.idUserGroup === idUserGroup);
        if (index >= 0) {
            this.groups.splice(index, 1, userGroup);
        } else {
            this.groups.push(userGroup);
        }
    }

    async inviteUsersToGroup(idUserGroup, inviteData) {
        const {data} = await this.ApiService.call2(`/users/groups/${idUserGroup}/add-users`, inviteData, {throwOnError: true});
        await this.reloadUserGroups();
        return data.status;
    }

    async removeUserFromGroup(idUserGroup, idUser) {
        const payload = {
            idUser,
        };
        await this.ApiService.call2(`/users/groups/${idUserGroup}/remove-users`, payload, {throwOnError: true});
        const group = this.groups.find(g => g.idUserGroup === idUserGroup);
        // Update local group entry
        if (group) {
            const index = group.members.findIndex(member => member.idUser === idUser);
            if (index >= 0) {
                group.members.splice(index, 1);
            }
        }
    }

    async removeInvite(idUserGroup, email) {
        const payload = {
            email: email,
        };
        await this.ApiService.call2(`/users/groups/${idUserGroup}/delete-invite`, payload, {throwOnError: true});
        const group = this.groups.find(g => g.idUserGroup === idUserGroup);
        // Update local group entry
        if (group) {
            const index = group.invites.findIndex(member => member.email === email);
            if (index >= 0) {
                group.invites.splice(index, 1);
            }
        }
    }


    async linkSites(idUserGroup, sites) {
        const payload = {
            sites,
        };
        await this.ApiService.call2(`/users/groups/${idUserGroup}/update-access`, payload, {throwOnError: true});
        await this.reloadUserGroups();
    }

    async linkSiteGroups(idUserGroup, siteGroups) {
        const payload = {
            siteGroups,
        };
        await this.ApiService.call2(`/users/groups/${idUserGroup}/update-access`, payload, {throwOnError: true});
        await this.reloadUserGroups();
    }

    async setSiteAccessLevel(idUserGroup, idSite, accessLevel) {
        const payload = {
            sites: [{
                idSite,
                accessLevel,
            }],
        };
        await this.ApiService.call2(`/users/groups/${idUserGroup}/update-access`, payload, {throwOnError: true});
        const team = this.groups.find(g => g.idUserGroup === idUserGroup);
        if (team) {
            const site = team.sites.find(s => s.idSite === idSite);
            if (site) {
                site.accessLevel = accessLevel;
            }
        }
    }

    async setSiteGroupAccessLevel(idUserGroup, idSiteGroup, accessLevel) {
        const payload = {
            siteGroups: [{
                idSiteGroup,
                accessLevel,
            }],
        };
        await this.ApiService.call2(`/users/groups/${idUserGroup}/update-access`, payload, {throwOnError: true});
        const team = this.groups.find(g => g.idUserGroup === idUserGroup);
        if (team) {
            const site = team.siteGroups.find(s => s.idSiteGroup === idSiteGroup);
            if (site) {
                site.accessLevel = accessLevel;
            }
        }
    }

    async unlinkSite(idUserGroup, idSite) {
        await this.ApiService.call2(`/users/groups/${idUserGroup}/update-access`, {
            sites: [{
                idSite,
                accessLevel: null,
            }],
        }, {throwOnError: true});
        const team = this.groups.find(g => g.idUserGroup === idUserGroup);
        if (team) {
            const index = team.sites.findIndex(s => s.idSite === idSite);
            team.sites.splice(index, 1);
        }
    }

    async unlinkSiteGroup(idUserGroup, idSiteGroup) {
        await this.ApiService.call2(`/users/groups/${idUserGroup}/update-access`, {
            siteGroups: [{
                idSiteGroup,
                accessLevel: null,
            }],
        }, {throwOnError: true});
        const team = this.groups.find(g => g.idUserGroup === idUserGroup);
        if (team) {
            const index = team.siteGroups.findIndex(s => s.idSiteGroup === idSiteGroup);
            team.siteGroups.splice(index, 1);
        }
    }

    async setUserGroupAccessLevelAsSiteAdmin(idSite, idUserGroup, accessLevel) {
        await this.ApiService.call2(`/installations/${idSite}/user-group-access-level`, {
            idUserGroup,
            accessLevel,
        });
        if (this.promise) {
            this.reloadUserGroups();
        }
        if (this.visiblePromise) {
            this.reloadVisible();
        }
    }

    async linkUserGroupsToSiteAsSiteAdmin(idSite, userGroups) {
        await this.ApiService.call2(`/installations/${idSite}/link-user-groups`, {
            userGroups,
        });
        if (this.promise) {
            this.reloadUserGroups();
        }
        if (this.visiblePromise) {
            this.reloadVisible();
        }
    }

    async updateUser(idUserGroup, idUser, {receivesAlarmNotifications}) {
        const payload = {
            idUser,
        };
        if (receivesAlarmNotifications !== undefined) {
            payload.receivesAlarmNotifications = !!receivesAlarmNotifications;
        }
        await this.ApiService.call2(`/users/groups/${idUserGroup}/update-user`, payload);
    }

    updateCachedUserGroupFields(idUserGroup, fields){
        this.groups = this.groups.map(group => group.idUserGroup === idUserGroup ? {...group, ...fields} : group);
    }
}

angular.module('vrm.core').service('UserGroupsService', UserGroupsService);
